import { useMutation } from "@tanstack/react-query";
import { explainFunctionError, useSupabase, useTenant } from "@how/app-kit";

/**
 * The Planning Center reader (ADR 0044), driven from the browser one page at a time. The church's application id and
 * secret live in the component's state for the run and travel with each call; nothing here stores them.
 */

export type NoteChoice = "confidential" | "ordinary" | "skip";

export interface PlanningCenterCredentials {
  readonly appId: string;
  readonly secret: string;
}

export interface PlanningCenterPreparation {
  readonly organization: { readonly name: string };
  readonly people: number;
  readonly noteCategories: readonly { readonly id: string; readonly name: string }[];
  readonly campuses: { readonly matched: readonly string[]; readonly unmatched: readonly string[] };
  readonly fields: { readonly matched: readonly string[]; readonly unmatched: readonly string[] };
  readonly notBrought: readonly string[];
}

interface PageAnswer {
  readonly staged: number;
  readonly skipped?: number;
  readonly total: number;
  readonly next: number | null;
}

export interface PlanningCenterProgress {
  readonly phase: "people" | "notes";
  readonly done: number;
  readonly total: number;
}

function useTenantId(): string {
  const tenant = useTenant();
  return tenant.status === "resolved" ? tenant.tenant.tenantId : "";
}

async function call<T>(
  supabase: ReturnType<typeof useSupabase>,
  body: Record<string, unknown>,
): Promise<T> {
  const result = await supabase.functions.invoke<T>("import-from-planning-center", { body });
  if (result.error) {
    const context = (result.error as { context?: Response }).context;
    throw await explainFunctionError(result.error, context);
  }
  return result.data as T;
}

/** Step one: with the token, what the run would bring, before an import exists. */
export function usePreparePlanningCenter() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useMutation({
    mutationFn: (credentials: PlanningCenterCredentials) =>
      call<PlanningCenterPreparation>(supabase, {
        action: "prepare",
        tenant_id: tenantId,
        app_id: credentials.appId,
        secret: credentials.secret,
      }),
  });
}

const failed = (error: { message: string; code?: string } | null) => {
  if (error !== null) throw Object.assign(new Error(error.message), { code: error.code });
};

/**
 * Step two: the run. Creates the import, walks the people pages, then the notes pages, then closes staging so the
 * dry-run report is fixed. Each page is one short call; a page the function could not finish is retried once, and a
 * run that stops can be started again (a page staged twice is staged once).
 */
export function useRunPlanningCenterImport() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useMutation({
    mutationFn: async (input: {
      readonly credentials: PlanningCenterCredentials;
      readonly organizationName: string;
      readonly people: number;
      readonly noteCategories: Readonly<Record<string, NoteChoice>>;
      readonly onProgress: (progress: PlanningCenterProgress) => void;
    }): Promise<string> => {
      const created = await supabase.rpc("create_import", {
        p_tenant_id: tenantId,
        p_file_name: `Planning Center: ${input.organizationName}`.slice(0, 255),
        p_source: "planning_center",
      });
      failed(created.error);
      const importId = created.data as string;
      const common = {
        tenant_id: tenantId,
        import_id: importId,
        app_id: input.credentials.appId,
        secret: input.credentials.secret,
      };

      const walk = async (phase: "people" | "notes", extra: Record<string, unknown>) => {
        let offset = 0;
        let total = phase === "people" ? input.people : 0;
        let done = 0;
        input.onProgress({ phase, done, total });
        for (;;) {
          let answer: PageAnswer;
          try {
            answer = await call<PageAnswer>(supabase, {
              action: phase,
              ...common,
              offset,
              ...extra,
            });
          } catch (error) {
            // One more try for a page Planning Center was slow with; a second failure stops the run.
            if (!(error instanceof Error && /slow down|did not answer/.test(error.message)))
              throw error;
            await new Promise((resolve) => setTimeout(resolve, 5000));
            answer = await call<PageAnswer>(supabase, {
              action: phase,
              ...common,
              offset,
              ...extra,
            });
          }
          done += answer.staged + (answer.skipped ?? 0);
          total = answer.total;
          input.onProgress({ phase, done, total });
          if (answer.next === null) break;
          offset = answer.next;
        }
      };

      await walk("people", {});
      const anyNotes = Object.values(input.noteCategories).some((choice) => choice !== "skip");
      if (anyNotes) await walk("notes", { note_categories: input.noteCategories });

      const finished = await supabase.rpc("finish_import_staging", { p_import_id: importId });
      failed(finished.error);
      return importId;
    },
  });
}
